import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useLocation, useParams } from 'react-router-dom';
import { Link } from '@bighealth/react-limbix-ui';
import { useFormContext } from 'react-hook-form';

import { SoleraAccountCreationParams } from '../SoleraAccountCreationPage';
import { SoleraAccountCreationStep } from '../soleraAccountCreationState';

import { SET_EMAIL_VERIFIED, SEND_EMAIL_CHANGE_VERIFICATION } from '@/apollo/mutations';
import { useUserUid } from '@/hooks/useUserUid';
import { getPdtUid } from '@/utils/productHelpers';

type Props = {
  careTeamUid: string;
  onNext: (step: SoleraAccountCreationStep, data?: { careTeamUid: string; token?: string }) => void;
  onError: () => void;
};

const EmailVerification = ({ careTeamUid, onNext, onError }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { product } = useParams<SoleraAccountCreationParams>();
  const { setUserUid } = useUserUid();
  const { getValues } = useFormContext();
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token');
  const careTeamUidFromUrl = searchParams.get('careTeamUid');

  // Use careTeamUid from URL if available, then from localStorage, then from prop
  const careTeamUidFromStorage = typeof window !== 'undefined'
    ? localStorage.getItem('solera_careTeamUid')
    : null;
  const effectiveCareTeamUid = careTeamUidFromUrl || careTeamUidFromStorage || careTeamUid;

  const [setEmailVerified] = useMutation(SET_EMAIL_VERIFIED);
  const [sendEmailChangeVerification] = useMutation(SEND_EMAIL_CHANGE_VERIFICATION);

  // Get email from form context or localStorage
  const emailFromForm = getValues('email');
  const emailFromStorage = typeof window !== 'undefined'
    ? localStorage.getItem('solera_email')
    : null;
  const email = emailFromForm || emailFromStorage || '';

  // Get original email from localStorage
  const originalEmail = typeof window !== 'undefined'
    ? localStorage.getItem('solera_originalEmail')
    : null;

  const handleVerifyEmail = useCallback(async () => {
    if (!token || !effectiveCareTeamUid) {
      return;
    }

    try {
      const response = await setEmailVerified({
        variables: {
          careTeamUid: effectiveCareTeamUid,
          token,
        },
      });

      const result = response?.data?.setEmailVerified;

      if (result?.success) {
        if (result.userUid) {
          setUserUid(result.userUid);
        }
        setVerificationComplete(true);
        // Get token from localStorage before clearing
        const storedToken = typeof window !== 'undefined'
          ? localStorage.getItem('solera_token')
          : null;

        // Clear stored values after successful verification
        if (typeof window !== 'undefined') {
          localStorage.removeItem('solera_careTeamUid');
          localStorage.removeItem('solera_email');
          localStorage.removeItem('solera_originalEmail');
          localStorage.removeItem('solera_token');
        }
        onNext('CreatePassword', {
          careTeamUid: effectiveCareTeamUid,
          token: storedToken || undefined,
        });
      } else {
        console.error('Error verifying email: ', response?.errors);
        setError(true);
        onError();
      }
    } catch (e) {
      console.error('Error verifying email:', e);
      setError(true);
      onError();
    }
  }, [token, effectiveCareTeamUid, setEmailVerified, setUserUid, onNext, onError]);

  useEffect(() => {
    if (token && effectiveCareTeamUid && !verificationComplete) {
      handleVerifyEmail();
    }
  }, [token, effectiveCareTeamUid, verificationComplete, handleVerifyEmail]);

  const onClickResendEmail = useCallback(async () => {
    if (!email || !originalEmail) {
      return;
    }

    // Get token from localStorage or URL
    const storedToken = typeof window !== 'undefined'
      ? localStorage.getItem('solera_token')
      : null;
    const tokenToUse = token || storedToken;

    if (!tokenToUse) {
      setError(true);
      setEmailSent(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    try {
      const result = await sendEmailChangeVerification({
        variables: {
          previousEmail: originalEmail,
          email,
          pdtUid: getPdtUid(product),
          token: tokenToUse,
        },
      });
      if (result?.errors || !result?.data?.sendEmailChangeVerification?.success) {
        setError(true);
        setEmailSent(false);
      } else {
        setEmailSent(true);
      }
    } catch (e) {
      // Optional: log for debugging/monitoring
      console.error('Error resending Solera verification email:', e);
      setError(true);
      setEmailSent(false);
    } finally {
      setLoading(false);
    }
  }, [email, originalEmail, product, sendEmailChangeVerification, token]);

  if (token && effectiveCareTeamUid) {
    // Token exists, verifying...
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // No token, show check your email message
  return (
    <Box>
      <Typography variant="h3" style={{ fontWeight: 700, marginTop: 32 }}>
        {t('solera.account.check_email_title', 'Check your email')}
      </Typography>
      <Typography style={{ marginTop: 12, marginBottom: 16 }}>
        {email
          ? t(
            'solera.account.check_email_message',
            { email },
          )
          : t(
            'solera.account.check_email_message_no_email',
          )}
      </Typography>
      {error && (
        <Typography color="error" style={{ marginTop: 16, marginBottom: 16 }}>
          {t('solera.account.email_verification_error')}
        </Typography>
      )}
      {emailSent && (
        <Typography style={{ marginTop: 16, marginBottom: 16 }}>
          {t('solera.account.email_sent')}
        </Typography>
      )}
      <Box style={{ marginTop: 24 }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <Link.ButtonLink onClick={onClickResendEmail}>
            {t('solera.account.resend_email')}
          </Link.ButtonLink>
        )}
      </Box>
    </Box>
  );
};

export default EmailVerification;
